/**
 * Bundle Builder — isolated vanilla JS.
 * No dependencies, no jQuery, no external libraries.
 * Scoped entirely to <bundle-builder> custom elements so it can never
 * collide with existing theme scripts or bundle app scripts.
 */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('bundle-builder')) {
    return;
  }

  class BundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.selections = new Map(); // productId -> { variantId, title, price, image, quantity }
      this.maxQuantity = parseInt(this.dataset.bundleQuantity, 10) || 5;
      this.bundlePrice = parseFloat(this.dataset.bundlePrice) || 0;
      this.bundleTag = this.dataset.bundleTag || 'bundle';
      this.redirectToCheckout = this.dataset.redirectToCheckout === 'true';
      this.isSubmitting = false;
    }

    connectedCallback() {
      this.grid = this.querySelector('[data-bundle-grid]');
      this.progressFill = this.querySelector('[data-bundle-progress-fill]');
      this.progressBar = this.querySelector('[data-bundle-progressbar]');
      this.countEls = this.querySelectorAll('[data-bundle-count]');
      this.pctEl = this.querySelector('[data-bundle-pct]');
      this.stickyBar = this.querySelector('[data-bundle-sticky-bar]');
      this.stickyCountEl = this.querySelector('[data-bundle-sticky-count]');
      this.checkoutBtn = this.querySelector('[data-bundle-checkout-btn]');
      this.checkoutSpinner = this.querySelector('[data-bundle-checkout-spinner]');
      this.viewSelectedBtn = this.querySelector('[data-bundle-view-selected]');
      this.modal = this.querySelector('[data-bundle-modal]');
      this.modalList = this.querySelector('[data-bundle-modal-list]');
      this.modalEmpty = this.querySelector('[data-bundle-modal-empty]');
      this.toast = this.querySelector('[data-bundle-toast]');
      this.modalCloseEls = this.querySelectorAll('[data-bundle-modal-close]');

      this.bindGridEvents();
      this.bindStickyBarEvents();
      this.bindModalEvents();
      this.renderState();
    }

    bindGridEvents() {
      if (!this.grid) return;
      this.grid.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-bundle-select-btn]');
        if (!btn) return;
        const card = btn.closest('[data-bundle-product-card]');
        if (!card || card.dataset.available === 'false') return;
        this.toggleSelection(card);
      });
    }

    bindStickyBarEvents() {
      if (this.viewSelectedBtn) {
        this.viewSelectedBtn.addEventListener('click', () => this.openModal());
      }
      if (this.checkoutBtn) {
        this.checkoutBtn.addEventListener('click', () => this.handleCheckout());
      }
    }

    bindModalEvents() {
      this.modalCloseEls.forEach((el) => {
        el.addEventListener('click', () => this.closeModal());
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeModal();
      });
      if (this.modalList) {
        this.modalList.addEventListener('click', (event) => {
          const removeBtn = event.target.closest('[data-bundle-remove]');
          const incBtn = event.target.closest('[data-bundle-qty-inc]');
          const decBtn = event.target.closest('[data-bundle-qty-dec]');
          if (removeBtn) {
            this.removeSelection(removeBtn.dataset.productId);
          } else if (incBtn) {
            this.changeQuantity(incBtn.dataset.productId, 1);
          } else if (decBtn) {
            this.changeQuantity(decBtn.dataset.productId, -1);
          }
        });
      }
    }

    get totalSelectedQuantity() {
      let total = 0;
      this.selections.forEach((item) => {
        total += item.quantity;
      });
      return total;
    }

    toggleSelection(card) {
      const productId = card.dataset.productId;
      if (this.selections.has(productId)) {
        this.removeSelection(productId);
        return;
      }

      if (this.totalSelectedQuantity >= this.maxQuantity) {
        this.showToast('You can only pick ' + this.maxQuantity + ' products.');
        return;
      }

      this.selections.set(productId, {
        variantId: card.dataset.variantId,
        title: card.dataset.productTitle,
        handle: card.dataset.productHandle,
        price: parseFloat(card.dataset.productPrice) || 0,
        image: card.dataset.productImage,
        quantity: 1,
      });

      card.classList.add('bundle-card--selected');
      const btn = card.querySelector('[data-bundle-select-btn]');
      if (btn) btn.setAttribute('aria-pressed', 'true');

      this.renderState();
    }

    removeSelection(productId) {
      const card = this.grid
        ? this.grid.querySelector('[data-product-id="' + CSS.escape(productId) + '"]')
        : null;
      if (card) {
        card.classList.remove('bundle-card--selected');
        const btn = card.querySelector('[data-bundle-select-btn]');
        if (btn) btn.setAttribute('aria-pressed', 'false');
      }
      this.selections.delete(productId);
      this.renderState();
    }

    changeQuantity(productId, delta) {
      const item = this.selections.get(productId);
      if (!item) return;

      if (delta > 0 && this.totalSelectedQuantity >= this.maxQuantity) {
        this.showToast('You can only pick ' + this.maxQuantity + ' products.');
        return;
      }

      item.quantity += delta;
      if (item.quantity <= 0) {
        this.removeSelection(productId);
        return;
      }

      this.renderState();
    }

    renderState() {
      const count = this.totalSelectedQuantity;
      const pct = Math.min(100, Math.round((count / this.maxQuantity) * 100));

      this.countEls.forEach((el) => {
        el.textContent = count;
      });
      if (this.pctEl) this.pctEl.textContent = pct + '%';
      if (this.progressFill) {
        this.progressFill.style.width = pct + '%';
        this.progressFill.classList.toggle('is-complete', pct === 100);
      }
      if (this.progressBar) {
        this.progressBar.setAttribute('aria-valuenow', String(pct));
      }
      if (this.stickyCountEl) this.stickyCountEl.textContent = count;

      const atLimit = count >= this.maxQuantity;
      if (this.grid) {
        this.grid
          .querySelectorAll('[data-bundle-product-card]')
          .forEach((card) => {
            const isSelected = card.classList.contains('bundle-card--selected');
            const btn = card.querySelector('[data-bundle-select-btn]');
            const isSoldOut = card.dataset.available === 'false';
            card.classList.toggle('bundle-card--limit-reached', atLimit && !isSelected);
            if (btn && !isSoldOut) {
              btn.disabled = atLimit && !isSelected;
            }
          });
      }

      if (this.stickyBar) {
        this.stickyBar.classList.toggle('is-visible', count > 0);
        this.stickyBar.setAttribute('aria-hidden', count > 0 ? 'false' : 'true');
      }

      if (this.checkoutBtn) {
        this.checkoutBtn.disabled = count !== this.maxQuantity || this.isSubmitting;
      }

      this.renderModal();
    }

    renderModal() {
      if (!this.modalList) return;

      const items = Array.from(this.selections.entries());
      this.modalList
        .querySelectorAll('.bundle-modal__item')
        .forEach((el) => el.remove());

      if (this.modalEmpty) {
        this.modalEmpty.style.display = items.length ? 'none' : 'block';
      }

      const count = this.totalSelectedQuantity;
      const atLimit = count >= this.maxQuantity;

      items.forEach(([productId, item]) => {
        const row = document.createElement('div');
        row.className = 'bundle-modal__item';
        row.innerHTML =
          '<img class="bundle-modal__item-image" src="' +
          this.escapeAttr(item.image || '') +
          '" alt="" width="60" height="60" loading="lazy">' +
          '<div class="bundle-modal__item-info">' +
          '<p class="bundle-modal__item-title">' +
          this.escapeHtml(item.title) +
          '</p>' +
          '<span class="bundle-modal__item-price">' +
          this.formatMoney(item.price) +
          '</span>' +
          '</div>' +
          '<div class="bundle-modal__item-qty">' +
          '<button type="button" class="bundle-modal__qty-btn" data-bundle-qty-dec data-product-id="' +
          this.escapeAttr(productId) +
          '" aria-label="Decrease quantity">&minus;</button>' +
          '<span class="bundle-modal__qty-value">' +
          item.quantity +
          '</span>' +
          '<button type="button" class="bundle-modal__qty-btn" data-bundle-qty-inc data-product-id="' +
          this.escapeAttr(productId) +
          '" aria-label="Increase quantity"' +
          (atLimit ? ' disabled' : '') +
          '>&plus;</button>' +
          '</div>' +
          '<button type="button" class="bundle-modal__item-remove" data-bundle-remove data-product-id="' +
          this.escapeAttr(productId) +
          '">Remove</button>';
        this.modalList.appendChild(row);
      });
    }

    openModal() {
      if (!this.modal) return;
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    showToast(message) {
      if (!this.toast) return;
      this.toast.textContent = message;
      this.toast.classList.add('is-visible');
      window.clearTimeout(this._toastTimeout);
      this._toastTimeout = window.setTimeout(() => {
        this.toast.classList.remove('is-visible');
      }, 2200);
    }

    formatMoney(amount) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
        return Shopify.formatMoney(Math.round(amount * 100), window.theme.moneyFormat);
      }
      return '₹' + amount.toFixed(2);
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value == null ? '' : String(value);
      return div.innerHTML;
    }

    escapeAttr(value) {
      return this.escapeHtml(value).replace(/"/g, '&quot;');
    }

    async handleCheckout() {
      if (this.isSubmitting) return;
      if (this.totalSelectedQuantity !== this.maxQuantity) {
        this.showToast('Select ' + this.maxQuantity + ' products to continue.');
        return;
      }

      this.isSubmitting = true;
      if (this.checkoutBtn) this.checkoutBtn.disabled = true;
      if (this.checkoutSpinner) this.checkoutSpinner.classList.remove('hidden');

      try {
        await this.clearCart();
        await this.addSelectionsToCart();

        if (this.redirectToCheckout) {
          window.location.href = '/checkout';
        } else {
          window.location.href = '/cart';
        }
      } catch (error) {
        this.showToast('Something went wrong. Please try again.');
        this.isSubmitting = false;
        if (this.checkoutSpinner) this.checkoutSpinner.classList.add('hidden');
        this.renderState();
      }
    }

    async clearCart() {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        throw new Error('Failed to clear cart');
      }
      return response.json();
    }

    async addSelectionsToCart() {
      const items = Array.from(this.selections.values()).map((item) => ({
        id: parseInt(item.variantId, 10),
        quantity: item.quantity,
        properties: {
          _bundle: this.bundleTag,
        },
      }));

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.description || 'Failed to add bundle to cart');
      }

      return response.json();
    }
  }

  window.customElements.define('bundle-builder', BundleBuilder);
})();
